/**
 * RFQ Supplier Search Dashboard Component
 * 
 * Displays all suppliers for RFQ with their products listed below each supplier using DataDashboard.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import DataDashboard from '../../../common/DataDashboard';
import UserLayout from '../../layout/UserLayout';
import HeaderActions from '../../../common/HeaderActions';
import { createStandardHeaderActions } from '../../../common/HeaderActions/helpers';
import ExportModal from '../../../common/ExportModal';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getCategoryById } from '../../../../utils/categories';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import { getRFQNavigationInfo, getRFQPageRoute, getRFQPageName } from '../../../../utils/rfqNavigation';
import { useRfqLock } from '../../../../hooks/useRfqLock';
import RfqLockedModal from '../../../common/RfqLockedModal';

interface UserRFQSupplierSearchDashboardProps {
  user: any;
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
}

interface GroupedSupplier {
  id: string;
  supplier_id: string;
  supplier_name: string;
  supplier_readable_id?: string;
  supplier_source?: string;
  rating?: number;
  products: Array<{
    supplier_product_id: string;
    product_name: string;
    price?: string | { min?: number; max?: number };
    currency?: string;
    category?: string;
    request_product_id: string;
  }>;
  company_specific_codes?: any[];
}

const UserRFQSupplierSearchDashboard: React.FC<UserRFQSupplierSearchDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout
}) => {
  const { token } = useAuth();
  const location = useLocation();

  // Extract requestId from URL
  const requestId = useMemo(() => {
    const pathParts = location.pathname.split('/');
    const openIndex = pathParts.indexOf('open');
    const rfqIndex = pathParts.indexOf('rfq');

    if (openIndex !== -1 && pathParts.length > openIndex + 1) {
      return pathParts[openIndex + 1];
    } else if (rfqIndex !== -1 && pathParts.length > rfqIndex + 1) {
      return pathParts[rfqIndex + 1];
    }

    return null;
  }, [location.pathname]);

  // Permission check - redirect if no rfq_operations
  useEffect(() => {
    if (user) {
      const permissions = user.permissions;
      const hasRfqOperations =
        (Array.isArray(permissions) ? permissions.includes('rfq_operations') : permissions?.rfq_operations) ||
        user.role === 'admin' ||
        (user as any).is_admin;

      if (!hasRfqOperations) {
        onNavigate('/dashboard/user/inbox/open');
      }
    }
  }, [user, onNavigate]);

  // Determine base route based on current path
  const baseRoute = useMemo(() => {
    if (location.pathname.startsWith('/dashboard/user/rfq/')) {
      return '/dashboard/user/rfq';
    }
    return '/dashboard/rfqs/open';
  }, [location.pathname]);

  const [products, setProducts] = useState<any[]>([]);
  const [groupedSuppliers, setGroupedSuppliers] = useState<GroupedSupplier[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [rfqSelectedSupplierIds, setRfqSelectedSupplierIds] = useState<string[]>([]);
  const [rfqSelectedSuppliersDetails, setRfqSelectedSuppliersDetails] = useState<any[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);


  // RFQ Lock Hook
  const { lockInfo: rfqLockInfo, showLockModal: showRfqLockModal, setShowLockModal: setShowRfqLockModal } = useRfqLock(requestId);

  // Get category name helper function
  const getCategoryName = (code: string): string => {
    const category = getCategoryById(code);
    return category?.name.tr || code;
  };

  // Load all suppliers for user's company
  const loadAllSuppliers = async () => {
    if (!requestId || !token) return;

    setIsLoading(true);
    setError(null);

    try {
      // Load all suppliers for user's company using the new endpoint
      const suppliersResponse = await fetch(API_ENDPOINTS.REQUEST_USER_SUPPLIERS(requestId), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!suppliersResponse.ok) {
        throw new Error(`HTTP error! status: ${suppliersResponse.status}`);
      }

      const suppliersData = await suppliersResponse.json();

      if (!suppliersData.success || !suppliersData.data) {
        throw new Error(suppliersData.message || 'Failed to load suppliers');
      }

      // Transform suppliers data to match the GroupedSupplier interface
      const transformedSuppliers: GroupedSupplier[] = suppliersData.data.map((supplier: any) => ({
        id: supplier.supplier_id || supplier._id,
        supplier_id: supplier.supplier_id || supplier._id,
        supplier_name: supplier.supplier_name || 'Unknown Supplier',
        supplier_readable_id: supplier.supplier_readable_id,
        supplier_source: supplier.supplier_source || 'manual',
        rating: supplier.rating || 0,
        products: supplier.products || [],  // Empty array for now since we're not filtering by products
        company_specific_codes: supplier.company_specific_codes || []
      }));

      setGroupedSuppliers(transformedSuppliers);

      // Load RFQ details to get selected supplier_ids
      const rfqResponse = await fetch(API_ENDPOINTS.REQUEST_V2_DETAIL(requestId), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const rfqResult = await rfqResponse.json();

      if (rfqResult.success && rfqResult.data) {
        // Redirect if phase is completed or select_supplier
        if (rfqResult.data.phase === 'completed') {
          onNavigate('/dashboard/user/inbox/finalized');
          return;
        }
        if (rfqResult.data.phase === 'select_supplier') {
          onNavigate('/dashboard/user/inbox/open');
          return;
        }

        const selectedSuppliers = (rfqResult.data.selected_supplier_ids || []).map(String);
        setRfqSelectedSupplierIds(selectedSuppliers);

        // Fetch supplier details for footer display
        if (selectedSuppliers.length > 0) {
          try {
            const supplierDetailsPromises = selectedSuppliers.map(async (supplierId: string) => {
              try {
                const response = await fetch(API_ENDPOINTS.SUPPLIER_DETAILS(supplierId), {
                  headers: {
                    'Authorization': `Bearer ${token}`
                  }
                });
                const result = await response.json();
                return result.success ? result.data : null;
              } catch (err) {
                console.error('Error fetching supplier:', supplierId, err);
                return null;
              }
            });

            const supplierDetails = (await Promise.all(supplierDetailsPromises)).filter(Boolean);
            setRfqSelectedSuppliersDetails(supplierDetails);
          } catch (err) {
            console.error('⚠️ Could not load supplier details:', err);
          }
        } else {
          setRfqSelectedSuppliersDetails([]);
        }
      }
    } catch (error: any) {
      console.error('Error fetching suppliers:', error);
      setError(error.message || 'Failed to load suppliers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAllSuppliers();
  }, [requestId, token]);

  // Toggle supplier expansion
  const toggleSupplierExpansion = (rowId: string) => {
    console.log('🔄 Toggle expansion for:', rowId);
    setExpandedSuppliers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
        console.log('✅ Collapsed:', rowId);
      } else {
        newSet.add(rowId);
        console.log('✅ Expanded:', rowId);
      }
      return newSet;
    });
  };

  // Handle supplier selection (single or bulk)
  const handleSelectionChange = async (newSelectedIds: string[]) => {
    if (!requestId || !token) return;

    // Optimistically update UI
    setRfqSelectedSupplierIds(newSelectedIds);

    // Save to backend
    try {
      const response = await fetch(`${API_ENDPOINTS.REQUEST_V2_DETAIL(requestId)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selected_supplier_ids: newSelectedIds
        })
      });

      const result = await response.json();

      if (!result.success) {
        console.error('❌ Failed to save:', result.error);
        // Revert on failure (though complex to revert perfectly, we could reload)
        // For now, just keep local state but log error
      } else {
        // Reload supplier details for footer if needed
        if (newSelectedIds.length > 0) {
          const detailsPromises = newSelectedIds.map(async (sid: string) => {
            // Check if we already have it in current details to avoid refetch
            const existing = rfqSelectedSuppliersDetails.find(d => d.supplier_id === sid);
            if (existing) return existing;

            try {
              const res = await fetch(API_ENDPOINTS.SUPPLIER_DETAILS(sid), {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const r = await res.json();
              return r.success ? r.data : null;
            } catch (e) {
              return null;
            }
          });

          const details = (await Promise.all(detailsPromises)).filter(Boolean);
          setRfqSelectedSuppliersDetails(details);
        } else {
          setRfqSelectedSuppliersDetails([]);
        }
      }
    } catch (error: any) {
      console.error('❌ Error saving:', error);
    }
  };

  // Format price for display
  const formatPrice = (price: any, currency?: string): string => {
    if (!price) return '-';
    let priceStr = '';
    if (typeof price === 'string') {
      priceStr = price;
    } else if (typeof price === 'object') {
      if (price.min !== undefined && price.max !== undefined) {
        priceStr = `${price.min} - ${price.max}`;
      } else if (price.min !== undefined) {
        priceStr = `${price.min}+`;
      } else if (price.max !== undefined) {
        priceStr = `up to ${price.max}`;
      } else {
        priceStr = String(price);
      }
    } else {
      priceStr = String(price);
    }

    if (currency && priceStr !== '-') {
      return `${priceStr} ${currency}`;
    }
    return priceStr;
  };

  // Column definitions for DataDashboard
  const supplierColumns = useMemo(() => [
    {
      key: 'supplier_name',
      title: 'Supplier Name',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '250px'
    },
    {
      key: 'supplier_readable_id',
      title: 'Supplier ID',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '150px',
      render: (value: string, row: GroupedSupplier) => value || row.supplier_id
    },
    {
      key: 'supplier_source',
      title: 'Source',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '120px',
      render: (value: string) => {
        if (value === 'ai_brave') {
          return (
            <span style={{
              padding: '4px 8px',
              backgroundColor: '#fff3cd',
              color: '#856404',
              borderRadius: '4px',
              fontSize: '12px'
            }}>
              AI Brave
            </span>
          );
        }
        return <span style={{ color: '#666' }}>Manual</span>;
      }
    },
    {
      key: 'approval_status',
      title: 'Approval Status',
      type: 'text' as const,
      sortable: true,
      editable: false,
      width: '140px',
      render: (value: string, row: GroupedSupplier) => {
        // Find status for current user's company
        const userCompanyId = user?.company_id;
        let status = 'pending';

        if (userCompanyId && row.company_specific_codes) {
          const codeEntry = row.company_specific_codes.find(
            (code: any) => String(code.company_id) === String(userCompanyId)
          );
          if (codeEntry?.approval_status) {
            status = String(codeEntry.approval_status).toLowerCase();
          }
        }

        // Render colored badge
        let backgroundColor = '#fff3cd'; // yellow/pending
        let color = '#856404';
        let label = 'Pending';

        if (status === 'approved') {
          backgroundColor = '#d1e7dd';
          color = '#0f5132';
          label = 'Approved';
        } else if (status === 'rejected') {
          backgroundColor = '#f8d7da';
          color = '#842029';
          label = 'Rejected';
        }

        return (
          <span style={{
            padding: '4px 8px',
            backgroundColor,
            color,
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'capitalize'
          }}>
            {label}
          </span>
        );
      }
    },
    {
      key: 'rating',
      title: 'Rating',
      type: 'number' as const,
      sortable: true,
      editable: false,
      width: '100px',
      render: (value: number) => value ? `${value}/5` : '-'
    },
    {
      key: 'products_count',
      title: 'Products',
      type: 'number' as const,
      sortable: true,
      editable: false,
      width: '100px',
      render: (value: any, row: GroupedSupplier) => `${row.products.length} product(s)`
    }
  ], []);

  // Get navigation info for Next/Back buttons
  const navInfo = useMemo(() => getRFQNavigationInfo(currentRoute), [currentRoute]);

  // Header actions for UserLayout
  const headerActions = (
    <HeaderActions
      {...createStandardHeaderActions({
        isEditing: false,
        onEditToggle: () => { },
        onSaveChanges: () => { },
        onExport: () => setShowExportModal(true),
        onDelete: () => { },
        selectedItemsCount: rfqSelectedSupplierIds.length,
        itemsPerPage,
        onItemsPerPageChange: setItemsPerPage,
        onFiltersToggle: () => setShowFiltersModal(!showFiltersModal),
        useIndividualComponents: true,
        showEditButton: false,
        showDeleteButton: false,
        showExportButton: true,
        showAddButton: false,
        customButtons: []
      })}
    />
  );

  // Navigation actions (Back/Next buttons)
  const navigationActions = navInfo ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {navInfo.previousPage && (
          <button
            onClick={() => onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.previousPage!))}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            ← Back to {getRFQPageName(navInfo.previousPage)}
          </button>
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {navInfo.nextPage && (
          <button
            onClick={() => onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.nextPage!))}
            style={{
              padding: '8px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            Next to {getRFQPageName(navInfo.nextPage)} →
          </button>
        )}
      </div>
    </div>
  ) : undefined;

  return (
    <UserLayout
      user={user}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle="Search Suppliers"
      pageDescription="Browse and select suppliers for your RFQ request"
      getDynamicSubItems={getDynamicSubItems}
      headerActions={headerActions}
      navigationActions={navigationActions}
    >
      <DataDashboard
        title="All Suppliers"
        collection="suppliers"
        data={groupedSuppliers}
        columns={supplierColumns}
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        onDataChange={setGroupedSuppliers}
        isLoading={isLoading}
        error={error}
        noLayout={true}
        getDynamicSubItems={getDynamicSubItems}
        showAddButton={false}
        showSearch={true}
        showSelection={true}
        showEditButton={false}
        showDeleteButton={false}
        showExportButton={true}
        expandableRows={true}
        expandedRows={expandedSuppliers}
        onRowExpand={toggleSupplierExpansion}
        itemsPerPage={itemsPerPage}
        onItemsPerPageChange={setItemsPerPage}
        paginationInfo={{
          page: 1,
          limit: itemsPerPage,
          total: groupedSuppliers.length,
          pages: Math.ceil(groupedSuppliers.length / itemsPerPage),
          has_next: groupedSuppliers.length > itemsPerPage,
          has_prev: false
        }}
        externalFiltersToggleState={showFiltersModal}
        onFiltersToggleStateChange={setShowFiltersModal}
        renderExpandedContent={(row: GroupedSupplier) => (
          <div style={{ padding: '4px 8px', textAlign: 'left', maxWidth: '400px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '6px',
              paddingBottom: '4px',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <div style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: 'left'
              }}>
                Products
              </div>
              <div style={{
                fontSize: '10px',
                fontWeight: 600,
                color: '#666',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: 'right'
              }}>
                Price
              </div>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px'
            }}>
              {row.products.map((product, idx) => (
                <div
                  key={`${product.supplier_product_id}-${idx}`}
                  style={{
                    padding: '6px 8px',
                    backgroundColor: '#fff',
                    border: '1px solid #e0e0e0',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    textAlign: 'left'
                  }}
                >
                  <div style={{
                    flex: 1,
                    minWidth: 0,
                    textAlign: 'left',
                    paddingRight: '12px'
                  }}>
                    <div style={{
                      fontWeight: 500,
                      marginBottom: '2px',
                      color: '#333',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      textAlign: 'left'
                    }}>
                      {product.product_name}
                    </div>
                    {product.category && (
                      <div style={{
                        fontSize: '11px',
                        color: '#666',
                        marginTop: '2px',
                        textAlign: 'left'
                      }}>
                        {getCategoryName(product.category)}
                      </div>
                    )}
                  </div>
                  <div style={{
                    minWidth: '80px',
                    textAlign: 'right',
                    flexShrink: 0
                  }}>
                    <div style={{
                      fontWeight: 600,
                      color: '#3b82f6',
                      fontSize: '13px',
                      textAlign: 'right'
                    }}>
                      {formatPrice(product.price, product.currency)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        onSelectionChange={handleSelectionChange}
        selectedItems={rfqSelectedSupplierIds}
        getRowClassName={(row: GroupedSupplier) => {
          return rfqSelectedSupplierIds.includes(row.supplier_id) ? 'selected-row' : '';
        }}
      />

      {/* Global Selected Suppliers Footer */}
      {rfqSelectedSupplierIds.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: '220px',
          right: 0,
          backgroundColor: '#f5f5f5',
          borderTop: '2px solid #ccc',
          padding: '15px 20px',
          zIndex: 1000
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '20px'
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>Selected Suppliers (GLOBAL - All Products): {rfqSelectedSupplierIds.length}</strong>
              <span style={{ fontSize: '12px', color: '#666', marginLeft: '8px' }}>
                (Auto-saved)
              </span>
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '8px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px'
              }}>
                {rfqSelectedSuppliersDetails.map((supplier: any, index: number) => (
                  <span key={supplier._id} style={{
                    backgroundColor: '#e3f2fd',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontFamily: 'monospace'
                  }}>
                    {index + 1}. {supplier.supplier_id} - {supplier.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        collection="suppliers"
        selectedItems={rfqSelectedSupplierIds.length > 0 ? groupedSuppliers.filter(s => rfqSelectedSupplierIds.includes(s.supplier_id)) : groupedSuppliers}
        customFields={supplierColumns.map(col => ({
          name: col.key,
          type: col.type,
          description: col.title
        }))}
        onExport={async (exportData: any) => {
          try {
            const response = await fetch(API_ENDPOINTS.EXPORT_DATA, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(exportData)
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Export failed: ${response.status} ${errorText}`);
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '');
            const baseFilename = exportData.filename || 'suppliers';
            a.download = `${baseFilename}_${dateStr}.${exportData.format === 'excel' ? 'xlsx' : 'csv'}`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            setShowExportModal(false);
          } catch (error: any) {
            console.error('Export error:', error);
            alert(`Export failed: ${error.message}`);
          }
        }}
      />

      <RfqLockedModal
        isOpen={showRfqLockModal}
        lockInfo={rfqLockInfo}
        onClose={() => setShowRfqLockModal(false)}
        onGoBack={() => {
          setShowRfqLockModal(false);
          onNavigate('/dashboard/user/inbox');
        }}
      />

    </UserLayout>
  );
};

export default UserRFQSupplierSearchDashboard;
